import { Modal, Textarea, Box, Text, Tabs, LoadingOverlay, Group } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconDeviceFloppy } from "@tabler/icons-react";
import type { FlowStepViewModel, FlowConfigViewModel } from "@models/doctype";
import { useStepEdit } from "@hooks/flow";
import { useTranslation } from "@hooks/useTranslation";
import { useToast } from "@hooks/useToast";
import { translateStepType } from "@utils/flowTypeTranslations";
import { LLMConfigTab } from "../LLMConfigTab";
import { OCRConfigTab } from "../OCRConfigTab";
import "./StepEditDialog.scss";

interface StepEditDialogProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
    step: FlowStepViewModel;
    docTypeId: string;
    flowConfig: FlowConfigViewModel;
    initialTab?: string;
}

export const StepEditDialog = ({
    open,
    onClose,
    onSave: onSaveCallback,
    step,
    docTypeId,
    flowConfig,
    initialTab,
}: StepEditDialogProps) => {
    const { t } = useTranslation();
    const { toast, showSuccess, showError, hideToast } = useToast();

    const {
        activeTab,
        hasChanges,
        providers,
        models,
        selectedProvider,
        selectedModel,
        isLoadingProviders,
        isLoadingModels,
        isLoading,
        systemPrompt,
        userPrompt,
        temperature,
        topP,
        topK,
        maxOutputTokens,
        thinkingBudget,
        seed,
        responseSchema,
        ocrOptions,
        setActiveTab,
        setSystemPrompt,
        setUserPrompt,
        setTemperature,
        setTopP,
        setTopK,
        setMaxOutputTokens,
        setThinkingBudget,
        setSeed,
        setResponseSchema,
        setOcrOptions,
        handleProviderChange,
        handleModelChange,
        handleSave,
        markAsChanged,
    } = useStepEdit({ open, step, flowConfig, docTypeId, initialTab });

    const onSave = async () => {
        const result = await handleSave();
        if (result) {
            showSuccess(t("admin.flowConfigs.messages.saveSuccess"));
            if (onSaveCallback) {
                onSaveCallback();
            }
            onClose();
        } else {
            showError(t("admin.flowConfigs.messages.saveFailed"));
        }
    };

    const tabLabels = [
        t("admin.flowConfigs.labels.llmConfig"),
        t("admin.flowConfigs.labels.systemPrompt"),
        t("admin.flowConfigs.labels.userPrompt"),
        t("admin.flowConfigs.labels.responseSchema"),
    ];

    return (
        <>
            <Modal
                opened={open}
                onClose={onClose}
                size="95%"
                className="step-edit-dialog"
                portalProps={{ target: "#admin-portal-root" }}
                fullScreen={false}
                styles={{
                    content: {
                        maxHeight: "calc(100vh - 60px)",
                        display: "flex",
                        flexDirection: "column",
                    },
                    header: {
                        flexShrink: 0,
                    },
                    body: {
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        overflow: "hidden",
                        paddingBottom: 0,
                    },
                }}
                title={
                    <Box>
                        <Text fw={600} size="lg">
                            Step {step.stepOrder}: {translateStepType(step.stepType, t)}
                        </Text>
                        {hasChanges && (
                            <Text size="xs" c="yellow">
                                Unsaved changes
                            </Text>
                        )}
                    </Box>
                }
                closeButtonProps={{ disabled: isLoading }}
            >
                <LoadingOverlay visible={isLoading} />
                <Box className="step-edit-dialog__content">
                    {step.llmStepConfig && (
                        <Tabs
                            value={activeTab}
                            onChange={(value) => setActiveTab(value || "0")}
                            className="step-edit-dialog__tabs"
                        >
                            <Tabs.List>
                                {tabLabels.map((label, index) => (
                                    <Tabs.Tab key={index} value={String(index)}>
                                        {label}
                                    </Tabs.Tab>
                                ))}
                            </Tabs.List>

                            <Tabs.Panel value="0" className="step-edit-dialog__tab-panel">
                                <LLMConfigTab
                                    isLoadingProviders={isLoadingProviders}
                                    isLoadingModels={isLoadingModels}
                                    providers={providers}
                                    models={models}
                                    selectedProvider={selectedProvider}
                                    selectedModel={selectedModel}
                                    temperature={temperature}
                                    topP={topP}
                                    topK={topK}
                                    maxOutputTokens={maxOutputTokens}
                                    thinkingBudget={thinkingBudget}
                                    seed={seed}
                                    onProviderChange={handleProviderChange}
                                    onModelChange={handleModelChange}
                                    onTemperatureChange={(value) => {
                                        setTemperature(value);
                                        markAsChanged();
                                    }}
                                    onTopPChange={(value) => {
                                        setTopP(value);
                                        markAsChanged();
                                    }}
                                    onTopKChange={(value) => {
                                        setTopK(value);
                                        markAsChanged();
                                    }}
                                    onMaxOutputTokensChange={(value) => {
                                        setMaxOutputTokens(value);
                                        markAsChanged();
                                    }}
                                    onThinkingBudgetChange={(value) => {
                                        setThinkingBudget(value);
                                        markAsChanged();
                                    }}
                                    onSeedChange={(value) => {
                                        setSeed(value);
                                        markAsChanged();
                                    }}
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="1" className="step-edit-dialog__tab-panel">
                                <Textarea
                                    label={t("admin.flowConfigs.labels.systemPrompt")}
                                    value={systemPrompt}
                                    onChange={(e) => {
                                        setSystemPrompt(e.currentTarget.value);
                                        markAsChanged();
                                    }}
                                    required
                                    minRows={30}
                                    className="step-edit-dialog__prompt-field"
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="2" className="step-edit-dialog__tab-panel">
                                <Textarea
                                    label={t("admin.flowConfigs.labels.userPrompt")}
                                    value={userPrompt}
                                    onChange={(e) => {
                                        setUserPrompt(e.currentTarget.value);
                                        markAsChanged();
                                    }}
                                    required
                                    minRows={30}
                                    className="step-edit-dialog__prompt-field"
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="3" className="step-edit-dialog__tab-panel">
                                <Textarea
                                    label={
                                        t("admin.flowConfigs.labels.responseSchema") + " (Optional)"
                                    }
                                    value={responseSchema}
                                    onChange={(e) => {
                                        setResponseSchema(e.currentTarget.value);
                                        markAsChanged();
                                    }}
                                    minRows={30}
                                    placeholder='{"type": "object", "properties": {...}}'
                                    className="step-edit-dialog__prompt-field"
                                />
                            </Tabs.Panel>
                        </Tabs>
                    )}

                    {!step.llmStepConfig && step.ocrStepConfig && (
                        <Box className="step-edit-dialog__tab-panel">
                            <OCRConfigTab
                                isLoadingProviders={isLoadingProviders}
                                isLoadingModels={isLoadingModels}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider}
                                selectedModel={selectedModel}
                                ocrOptions={ocrOptions}
                                onProviderChange={handleProviderChange}
                                onModelChange={handleModelChange}
                                onOcrOptionsChange={(value) => {
                                    setOcrOptions(value);
                                    markAsChanged();
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <Group justify="flex-end" mt="md" className="step-edit-dialog__actions">
                    <Button variant="subtle" onClick={onClose} disabled={isLoading}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={onSave}
                        leftSection={<IconDeviceFloppy size={16} />}
                        disabled={isLoading || !hasChanges}
                    >
                        {t("common.save")}
                    </Button>
                </Group>
            </Modal>

            {toast.open && (
                <Alert
                    color={toast.severity === "error" ? "red" : "green"}
                    onClose={hideToast}
                    withCloseButton
                    style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1000 }}
                >
                    {toast.message}
                </Alert>
            )}
        </>
    );
};
